import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AccountManagementService } from 'src/app/services/account-management.service';
import { OrganisationService } from 'src/app/services/organisation.service';
import { USER_FORM_CONSTANTS } from 'src/app/models/user-form.constants';
import { StorageService } from 'src/app/services/storage.service';

@Component({
    selector: 'app-account-form',
    templateUrl: './account-form.component.html',
    styleUrls: ['./account-form.component.scss'],
    standalone: false
})
export class AccountFormComponent implements OnInit {
  readonly constants = USER_FORM_CONSTANTS;
  accountId!: number;
  editMode: boolean = false;
  editAccountForm: FormGroup;
  addAccountForm: FormGroup;
  addnewAccountDetails!: FormArray;
  submitted: boolean = false;
  organisation: any = {};
  phonePattern = /^[6-9]\d{9}$/;
  // accountData: { id: number; name: string; type: string; emailId: string; noOfBranches: string; createdOn: string; isActive: boolean; }[];
  accountData: {
    account_id: number;
    account_name: string;
    created_on: string;
    active_status: boolean;
    branch_count: number;
  }[];
  previousContact: string;
  userRole: string;

  constructor(
    private activatedRouter: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private accountManagementService: AccountManagementService,
    private organisationService: OrganisationService,
    private storageService: StorageService
  ) {
    this.activatedRouter.params.subscribe((params) => {
      this.accountId = params['id'];
      if (this.accountId) {
        this.editMode = true;
        // this.getAccountDetail(this.accountId);
        this.editAccountDetails(this.accountId);
      }
    });

    this.editAccountForm = this.formBuilder.group({
      name: [
        '',
        [
          Validators.required,
          Validators.maxLength(100),
          Validators.pattern(/^[a-zA-Z0-9\s]+$/),
          this.noOnlySpacesValidator,
          this.noXssValidator,
        ],
      ],
      contact: [
        '',
        [Validators.required, Validators.pattern(this.phonePattern)],
      ],
      organizationName: ['', [Validators.required]],
    });
    this.addAccountForm = this.formBuilder.group({
      addnewAccountDetails: this.formBuilder.array([]),
    });
  }
  noOnlySpacesValidator(control: FormControl) {
    if (control.value && control.value.trim().length === 0) {
      return { noOnlySpaces: true };
    }
    return null; // Validation passes
  }
  noXssValidator(control: FormControl) {
    if (!control.value) {
      return null;
    }

    const value = control.value.toString().toLowerCase().trim();

    // Patterns to detect malicious content
    const xssPatterns = [
      /<script/i, // script tags
      /<\/script>/i, // closing script tags
      /javascript:/i, // javascript: protocol
      /onerror=/i, // onerror event
      /onload=/i, // onload event
      /onclick=/i, // onclick event
      /<iframe/i, // iframe tags
      /<object/i, // object tags
      /<embed/i, // embed tags
      /<img[^>]*on/i, // img with events
      /eval\(/i, // eval function
      /%3cscript/i, // encoded <script
      /&lt;script/i, // HTML entity encoded
    ];
    const isMalicious = xssPatterns.some((pattern) => pattern.test(value));

    return isMalicious ? { xssDetected: true } : null;
  }

  ngOnInit() {
    this.userRole = this.storageService.getRole() || '';
    this.addNewAccountDetails();
    this.getOrganisationDetails();
  }

  removeAccount(index: number) {
    this.accountDetailsArray().removeAt(index);
    this.addAccountForm.markAsUntouched();
    this.addAccountForm.markAsPristine();
    this.submitted = false;
    this.accountDetailsArray().updateValueAndValidity();
  }

  accountDetailsArray(): FormArray {
    return this.addAccountForm.get('addnewAccountDetails') as FormArray;
  }

  addNewAccountDetails() {
    if (this.addAccountForm.controls['addnewAccountDetails'].valid) {
      this.accountDetailsArray().push(this.initAccountDetails());
      // Get the newly added form group
      const newIndex = this.accountDetailsArray().length - 1;
      const newUserFormGroup = this.accountDetailsArray().at(
        newIndex
      ) as FormGroup;

      // Reset validation state
      this.resetFormValidationState(newUserFormGroup);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'New Account',
        detail: 'Please fill Account details before adding New',
      });
    }
  }
  private resetFormValidationState(formGroup: FormGroup) {
    formGroup.markAsUntouched();
    formGroup.markAsPristine();

    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control) {
        control.markAsUntouched();
        control.markAsPristine();
      }
    });
  }

  initAccountDetails() {
    return this.formBuilder.group({
      name: new FormControl('', [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(/^[a-zA-Z0-9\s]+$/),
        this.noOnlySpacesValidator,
        this.noXssValidator,
      ]),
      contact: [
        '',
        [
          Validators.required,
          Validators.pattern(this.phonePattern),
          Validators.maxLength(10),
          Validators.minLength(10),
        ],
      ],
      // type: new FormControl('', [Validators.required]),
      organizationName: new FormControl(this.organisation.name),
      // branches: new FormControl('', [Validators.required]),
    });
  }

  numberOnly(event: any) {
    const pattern = /[0-9\-]/;
    let inputChar = String.fromCharCode(event.charCode);
    if (event.keyCode != 8 && !pattern.test(inputChar)) {
      event.preventDefault();
    }
  }

  gotoDashboard() {
    this.userRole === 'CLIENT'
      ? this.router.navigate(['doqfy/dashboard'])
      : this.router.navigate(['doqfy/admin-dashboard']);
  }

  gotoListing() {
    this.router.navigate(['/doqfy/management/account-listing']);
  }

  saveAccount() {
    this.submitted = true;

    if (this.addAccountForm.invalid) {
    this.messageService.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Please fill the required field correctly',
    });
    return;
  }
    const addnewAccountDetailsFormArray = this.addAccountForm.get(
      'addnewAccountDetails'
    ) as FormArray;

    let hasXss = false;
    addnewAccountDetailsFormArray.controls.forEach(
      (abstractControl: AbstractControl) => {
        if (abstractControl instanceof FormGroup) {
          const formGroup = abstractControl as FormGroup;
          if (formGroup.get('name')?.hasError('xssDetected')) {
            hasXss = true;
          }
        }
      }
    );

    if (hasXss) {
      this.messageService.add({
        severity: 'error',
        summary: 'Security Alert',
        detail:
          'Malicious content detected! Script tags and harmful code are not allowed.',
      });
      return;
    }

    if (this.addAccountForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill the required field correctly',
      });
      return;
    }

    const accountDetailsArray: {
      name: string;
      contact_number: string;
    }[] = []; // Define the type of the array explicitly

    // Iterate over each FormGroup in the FormArray
    addnewAccountDetailsFormArray.controls.forEach(
      (abstractControl: AbstractControl) => {
        if (abstractControl instanceof FormGroup) {
          const formGroup = abstractControl as FormGroup;
          const accountName = formGroup.controls['name'].value;
          const contact = formGroup.controls['contact'].value; // Access the value of the 'name' control in each FormGroup
          accountDetailsArray.push({
            name: accountName,
            contact_number: contact,
          }); // Push an object with the 'name' value to the array
        }
      }
    );

    const data = {
      details: accountDetailsArray,
    };
    this.accountManagementService.CreateAccount(data).subscribe((res: any) => {
      this.messageService.add({
        severity: 'success',
        summary: 'Account added successfully!',
      });
      setTimeout(() => {
        this.router.navigate(['/doqfy/management/account-listing']);
      }, 1000);
    });
  }

  editAccountDetails(id: any) {
    this.accountManagementService.getAccountDetail(id).subscribe((res: any) => {
      const accountData = res.content[0];
      console.log(accountData);
      if (accountData) {
        this.editAccountForm.patchValue({
          name: accountData.account_name,
          contact: accountData.contact_number,
          organizationName: accountData.organisation_name,
        });
        this.previousContact = accountData.contact_number;
      }
    });
  }

  editAccount() {
    this.submitted = true;

    if (this.editAccountForm.get('name')?.hasError('xssDetected')) {
      this.messageService.add({
        severity: 'error',
        summary: 'Security Alert',
        detail:
          'Malicious content detected! Script tags and harmful code are not allowed.',
      });
      return;
    }

    if (this.editAccountForm.invalid) {
    this.markFormGroupTouched(this.editAccountForm);
    this.messageService.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Please fill the required field correctly',
    });
    return;
  }

      if (this.editAccountForm.invalid) {
    this.markFormGroupTouched(this.editAccountForm);
    return;
  }
  const accountName = this.sanitizeInput(this.editAccountForm.value.name);
  const contact = this.editAccountForm.value.contact;
  const data = {
      name: accountName,
      contact_number: contact,
    };
    const id = this.accountId;
    if (this.previousContact === data.contact_number) {
      delete data.contact_number;
    }
    this.accountManagementService
      .editAccountService(id, data)
      .subscribe((res: any) => {
        console.log(res);
        if (res.status === 200) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success!',
            detail: 'Account details updated successfully.',
          });
          setTimeout(() => {
            this.router.navigate(['/doqfy/management/account-listing']);
          }, 2000);
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'oops!',
            detail: 'Something went wrong',
          });
        }
      });
  }

   private markFormGroupTouched(formGroup: FormGroup) {
     Object.keys(formGroup.controls).forEach((field) => {
       const control = formGroup.get(field);
       control?.markAsTouched({ onlySelf: true });
       control?.updateValueAndValidity();
     });
   }
  
   private sanitizeInput(value: string): string {
     if (!value) return '';
     return value.replace(/<[^>]*>/g, '').trim();
   }

  getAccountDetails() {
    this.accountManagementService
      .getAccountDetails('')
      .subscribe((res: any) => {
        this.accountData = res.content;
      });
  }

  getOrganisationDetails() {
    this.organisationService.getOrganisationDetails().subscribe((res: any) => {
      this.organisation = res.content;
    });
  }

  numberOnlyForContact(event: any) {
    const pattern = /[0-9\-]/;
    let inputChar = String.fromCharCode(event.charCode);
    if (
      (event.keyCode != 8 && !pattern.test(inputChar)) ||
      event.keyCode == 45
    ) {
      event.preventDefault();
    } else if (event.target.value === '0') {
      event.preventDefault();
    }
  }

  handlePasteEvent(event: ClipboardEvent) {
    const paste = event.clipboardData.getData('text');
    const regex = new RegExp(this.constants.ALPHANUMERIC_SPACE_PATTERN);
    if (!regex.test(paste)) {
      event.preventDefault();
    }
  }

}
